import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { PageLoader } from '../../Components/LoadingSpinner';
import { useAuthState } from 'react-firebase-hooks/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../firebase';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useProfileUpdate } from '../../hooks/profile';
import './ProfilePage.css';

const ProfilePage: React.FC = () => {
    const [user, loading, error] = useAuthState(auth);

    // Set document title
    useDocumentTitle('Profil');

    const initialProfileData = {
        name: user?.displayName || '',
        email: user?.email || ''
    };

    const {
        profileData,
        profileLoading,
        profileError,
        profileSuccess,
        setProfileData,
        updateProfile,
        clearProfileError,
        clearProfileSuccess,
    } = useProfileUpdate(initialProfileData);

    // Notification email state
    const [notificationEmail, setNotificationEmail] = useState('');
    const [notificationEmailLoading, setNotificationEmailLoading] = useState(false);
    const [notificationEmailSaving, setNotificationEmailSaving] = useState(false);
    const [notificationEmailError, setNotificationEmailError] = useState<string | null>(null);
    const [notificationEmailSuccess, setNotificationEmailSuccess] = useState<string | null>(null);

    const getOwnEmailFn = httpsCallable<Record<string, never>, { success: boolean; email?: string }>(functions, 'getOwnEmail');
    const updateOwnEmailFn = httpsCallable<{ email: string }, { success: boolean; message: string }>(functions, 'updateOwnEmail');

    useEffect(() => {
        if (user) {
            setProfileData({
                name: user.displayName || '',
                email: user.email || ''
            });

            // Fetch notification email
            setNotificationEmailLoading(true);
            getOwnEmailFn({})
                .then((result) => {
                    setNotificationEmail(result.data.email || '');
                })
                .catch((err) => {
                    console.error('Failed to fetch notification email:', err);
                })
                .finally(() => {
                    setNotificationEmailLoading(false);
                });
        }
    }, [user, setProfileData]);

    const handleSaveNotificationEmail = async () => {
        setNotificationEmailSaving(true);
        setNotificationEmailError(null);
        setNotificationEmailSuccess(null);

        try {
            await updateOwnEmailFn({ email: notificationEmail });
            setNotificationEmailSuccess('E-postadress för aviseringar sparad!');
        } catch (err: any) {
            setNotificationEmailError(err.message || 'Kunde inte spara e-postadress');
        } finally {
            setNotificationEmailSaving(false);
        }
    };

    const handleProfileUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;
        try {
            await updateProfile(profileData.name, user);
        } catch (err) {
            // Error handled by hook
        }
    };

    if (loading) {
        return (
            <div className="profile-page">
                <PageLoader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="profile-page">
                <Container className="profile-page-container">
                    <Alert variant="danger">
                        <Alert.Heading>Fel vid laddning av profil</Alert.Heading>
                        <p>{error.message}</p>
                    </Alert>
                </Container>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="profile-page">
                <Container className="profile-page-container">
                    <Alert variant="warning">
                        <Alert.Heading>Inte inloggad</Alert.Heading>
                        <p>Du måste vara inloggad för att komma åt denna sida.</p>
                    </Alert>
                </Container>
            </div>
        );
    }

    return (
        <div className="profile-page">
            <Container className="profile-page-container">
                <div className="profile-page-header">
                    <h1 className="page-title">Profil</h1>
                    <p className="profile-page-description">Hantera din profilinformation</p>
                </div>

                <Row>
                    <Col lg={6} className="mb-4">
                        <Card className="dropshadow">
                            <Card.Header>
                                <h5 className="mb-0">Profilinformation</h5>
                            </Card.Header>
                            <Card.Body>
                                {profileError && (
                                    <Alert variant="danger" dismissible onClose={clearProfileError}>
                                        {profileError}
                                    </Alert>
                                )}
                                {profileSuccess && (
                                    <Alert variant="success" dismissible onClose={clearProfileSuccess}>
                                        {profileSuccess}
                                    </Alert>
                                )}

                                <Form onSubmit={handleProfileUpdate}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>E-postadress</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={profileData.email}
                                            disabled
                                            className="bg-light"
                                        />
                                        <Form.Text className="text-muted">
                                            E-postadressen kan inte ändras
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Namn</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={profileData.name}
                                            onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                                            placeholder="Ange ditt namn"
                                            required
                                        />
                                    </Form.Group>

                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={profileLoading || !profileData.name.trim()}
                                    >
                                        {profileLoading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Uppdaterar...
                                            </>
                                        ) : (
                                            'Uppdatera profil'
                                        )}
                                    </Button>
                                </Form>

                                <hr className="my-4" />

                                {/* Notification email section */}
                                <h6 className="mb-3">E-post för aviseringar</h6>
                                {notificationEmailError && (
                                    <Alert variant="danger" dismissible onClose={() => setNotificationEmailError(null)}>
                                        {notificationEmailError}
                                    </Alert>
                                )}
                                {notificationEmailSuccess && (
                                    <Alert variant="success" dismissible onClose={() => setNotificationEmailSuccess(null)}>
                                        {notificationEmailSuccess}
                                    </Alert>
                                )}

                                <Form.Group className="mb-3">
                                    <Form.Label>E-postadress för aviseringar</Form.Label>
                                    {notificationEmailLoading ? (
                                        <div className="py-2">
                                            <Spinner animation="border" size="sm" />
                                        </div>
                                    ) : (
                                        <Form.Control
                                            type="email"
                                            value={notificationEmail}
                                            onChange={(e) => setNotificationEmail(e.target.value)}
                                            placeholder="Ange e-postadress för aviseringar"
                                            disabled={notificationEmailSaving}
                                        />
                                    )}
                                    <Form.Text className="text-muted">
                                        Denna adress används för påminnelser och aviseringar. Den behöver inte vara samma som din inloggningsadress.
                                    </Form.Text>
                                </Form.Group>

                                <Button
                                    variant="primary"
                                    onClick={handleSaveNotificationEmail}
                                    disabled={notificationEmailSaving || notificationEmailLoading}
                                >
                                    {notificationEmailSaving ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Sparar...
                                        </>
                                    ) : (
                                        'Spara aviseringsadress'
                                    )}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={6} className="mb-4">
                        <Card className="dropshadow">
                            <Card.Header>
                                <h5 className="mb-0">Kontoinformation</h5>
                            </Card.Header>
                            <Card.Body>
                                <p><strong>Användar-ID:</strong> {user.uid}</p>
                                <p><strong>E-post verifierad:</strong> {user.emailVerified ? 'Ja' : 'Nej'}</p>
                                <p><strong>Konto skapat:</strong> {user.metadata.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString('sv-SE') : 'Okänt'}</p>
                                <p className="mb-0"><strong>Senast inloggad:</strong> {user.metadata.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleDateString('sv-SE') : 'Okänt'}</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default ProfilePage;
